import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  regulatoryNewsCategoryKey,
  regulatoryNewsTopicCheckboxId,
  type RegulatoryNewsCategory,
} from '@/shared/hooks/api/regulatoryNews'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { getRegulatoryNewsTopicDisplayLabel } from './utils/regulatoryNewsTopicDisplay'

type RegulatoryNewsTopicPickerProps = {
  topics: RegulatoryNewsCategory[]
  selectedKeys: Set<string>
  onToggle: (category: RegulatoryNewsCategory, checked: boolean) => void
  disabled?: boolean
}

export function RegulatoryNewsTopicPicker({
  topics,
  selectedKeys,
  onToggle,
  disabled = false,
}: RegulatoryNewsTopicPickerProps) {
  const { t, i18n } = useTranslation('regulatory-alerts')

  const sortedTopics = useMemo(
    () =>
      [...topics].sort((left, right) =>
        getRegulatoryNewsTopicDisplayLabel(left, t).localeCompare(
          getRegulatoryNewsTopicDisplayLabel(right, t),
          i18n.language
        )
      ),
    [topics, t, i18n.language]
  )

  return (
    <div className='space-y-2'>
      <p className='text-sm text-muted-foreground'>
        {t('topicPicker.selectedCount', { count: selectedKeys.size })}
      </p>
      <div className='space-y-2'>
        {sortedTopics.map((category) => {
          const key = regulatoryNewsCategoryKey(category)
          const checkboxId = regulatoryNewsTopicCheckboxId(category)
          const isChecked = selectedKeys.has(key)

          return (
            <div
              key={key}
              className='flex items-start gap-3 rounded-md border p-3'
            >
              <Checkbox
                id={checkboxId}
                checked={isChecked}
                disabled={disabled}
                onCheckedChange={(checked) => {
                  onToggle(category, checked === true)
                }}
              />
              <Label htmlFor={checkboxId} className='font-medium'>
                {getRegulatoryNewsTopicDisplayLabel(category, t)}
              </Label>
            </div>
          )
        })}
      </div>
    </div>
  )
}
